import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, DonkeyMark } from "@/components/Logo";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { VerdictBadge, ScoreBar, ScoreRing, ConfidenceBadge, EvidenceChip } from "@/components/DonkeyBits";
import { toast } from "sonner";
import {
  Lock, ArrowRight, Loader2, MessageSquare, Send, Download, CheckCircle2,
  X, Sparkles, ChevronLeft, ChevronDown, RefreshCw, Trophy, ExternalLink, Info, LayoutDashboard,
} from "lucide-react";

const SCORE_LABELS = {
  personal_fit: "Personal Fit",
  credibility_to_deliver: "Credibility to Deliver",
  customer_problem_strength: "Customer Problem Strength",
  demand_evidence: "Demand Evidence",
  customer_accessibility: "Customer Accessibility",
  speed_to_first_test: "Speed to First Test",
  startup_affordability: "Startup Affordability",
  margin_potential: "Margin Potential",
  delivery_feasibility: "Delivery Feasibility",
  differentiation: "Differentiation",
  recurring_revenue_potential: "Recurring Revenue Potential",
  regulatory_simplicity: "Regulatory Simplicity",
  scalability: "Scalability",
  evidence_quality: "Evidence Quality",
};

const sc = (v) => (v && typeof v === "object" ? v.score : v);

function List({ items, className = "" }) {
  return (
    <ul className={`space-y-2 ${className}`}>
      {(items || []).map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-donkey-warm/85 leading-relaxed">
          <span className="text-donkey-orange mt-1.5 shrink-0 w-1 h-1 rounded-full bg-donkey-orange" />
          {it}
        </li>
      ))}
    </ul>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">{label}</p>
      <div className="text-sm text-donkey-warm/90 leading-relaxed">{children}</div>
    </div>
  );
}

const DIM_META = [
  ["commercial_viability", "Commercial Viability", false],
  ["founder_capability", "Founder Capability", false],
  ["daily_activity_compatibility", "Daily-Activity Compatibility", false],
  ["financial_exposure", "Financial Exposure", true],
  ["evidence_quality", "Evidence Quality", false],
];
// good→green scale. inverted=true means Low is good (used for financial exposure)
function ratingTone(rating, inverted) {
  const r = (rating || "").toLowerCase();
  const good = inverted ? ["low"] : ["strong"];
  const bad = inverted ? ["high"] : ["weak"];
  if (good.includes(r)) return "text-donkey-green border-donkey-green/40 bg-donkey-green/10";
  if (bad.includes(r)) return "text-donkey-red border-donkey-red/40 bg-donkey-red/10";
  return "text-donkey-amber border-donkey-amber/40 bg-donkey-amber/10";
}
const RECO_LABELS = {
  adapt_offer: "Adapt the offer",
  change_delivery_model: "Change the delivery model",
  obtain_more_evidence: "Obtain further evidence",
  different_opportunity: "Choose a different opportunity",
  reject: "Reject the opportunity",
};

function VerdictDimensions({ dims }) {
  if (!dims || typeof dims !== "object") return null;
  return (
    <div data-testid="verdict-dimensions">
      <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-3">Verdict dimensions — assessed separately</p>
      <div className="space-y-3">
        {DIM_META.map(([key, label, inverted]) => {
          const d = dims[key];
          if (!d) return null;
          return (
            <div key={key} className="rounded-xl bg-donkey-bg border border-white/10 p-4" data-testid={`verdict-dimension-${key}`}>
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <p className="text-sm font-semibold text-white">{label}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border ${ratingTone(d.rating, inverted)}`}>{d.rating}</span>
                  {d.confidence && <ConfidenceBadge level={d.confidence} />}
                </div>
              </div>
              {d.explanation && <p className="text-sm text-donkey-warm/90 leading-relaxed">{d.explanation}</p>}
              {d.evidence && (
                <p className="text-xs text-donkey-grey mt-2"><span className="text-white/70">Evidence: </span>{d.evidence}</p>
              )}
              {d.assumptions && (
                <p className="text-xs text-donkey-grey mt-1"><span className="text-white/70">Assumptions / unknowns: </span>{d.assumptions}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MismatchCallout({ mismatch }) {
  if (!mismatch || !mismatch.mismatch) return null;
  const area = DIM_META.find(([k]) => k === mismatch.problem_area);
  return (
    <div className="rounded-xl bg-donkey-amber/10 border border-donkey-amber/30 p-4" data-testid="founder-market-mismatch">
      <div className="flex items-center gap-2 mb-1.5">
        <Info className="w-4 h-4 text-donkey-amber" />
        <p className="text-xs uppercase tracking-[0.15em] text-donkey-amber font-semibold">Founder / market mismatch</p>
      </div>
      {area && <p className="text-sm text-white/90 mb-1">Weak link: <span className="font-semibold">{area[1]}</span></p>}
      {mismatch.explanation && <p className="text-sm text-donkey-warm/90 leading-relaxed">{mismatch.explanation}</p>}
      {mismatch.recommendation && mismatch.recommendation !== "none" && (
        <p className="text-sm text-white mt-2"><span className="text-donkey-grey">Recommendation: </span><span className="font-semibold text-donkey-amber">{RECO_LABELS[mismatch.recommendation] || mismatch.recommendation}</span></p>
      )}
    </div>
  );
}

function OpportunityDetail({ opp, index, reportId, onSelect, selected }) {
  const s = opp.scores || {};
  return (
    <div className="card-donkey rounded-2xl p-6 sm:p-8" data-testid="opportunity-detail">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-2">{opp.rank_label}</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">{opp.name}</h2>
          <p className="text-donkey-grey mt-2 max-w-2xl">{opp.proposition}</p>
        </div>
        <div className="text-center shrink-0">
          <ScoreRing value={opp.overall} size={96} />
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-2">
        <TabsList className="flex flex-wrap h-auto bg-donkey-bg border border-white/10 p-1 gap-1">
          {[
            ["overview", "Overview"], ["why", "Why You"], ["customer", "Customer"],
            ["offer", "Offer"], ["evidence", "Evidence"], ["adaptability", "Adaptability"],
            ["paths", "Paths"], ["evidenceplan", "Evidence Plan"], ["validation", "Validation"],
            ["numbers", "Numbers"], ["obstacles", "Obstacles"], ["plan", "Action Plan"],
            ["verdict", "Donkey's Verdict"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v}
              className="text-xs data-[state=active]:bg-donkey-orange data-[state=active]:text-donkey-black text-donkey-grey"
              data-testid={`tab-${v}`}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-6">
          <TabsContent value="overview" className="space-y-5">
            <Field label="Proposition">{opp.proposition}</Field>
            <Field label="Solution">{opp.solution}</Field>
            <div className="grid sm:grid-cols-3 gap-4 pt-2">
              <div className="rounded-xl bg-donkey-bg border border-white/10 p-4">
                <p className="text-xs text-donkey-grey">Confidence</p>
                <p className="text-donkey-warm font-medium mt-1">{opp.confidence}</p>
              </div>
              <div className="rounded-xl bg-donkey-bg border border-white/10 p-4">
                <p className="text-xs text-donkey-grey">Launch difficulty</p>
                <p className="text-donkey-warm font-medium mt-1">{opp.launch_difficulty}</p>
              </div>
              <div className="rounded-xl bg-donkey-bg border border-white/10 p-4">
                <p className="text-xs text-donkey-grey">Time to validation</p>
                <p className="text-donkey-warm font-medium mt-1">{opp.time_to_first_validation}</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="why" className="space-y-5">
            <Field label="Why this matches you">{opp.why_you}</Field>
            <Field label="Evidence you could credibly deliver it">{opp.credibility_evidence}</Field>
            {opp.combination_advantage && (
              <div className="rounded-xl bg-donkey-bg border border-white/10 p-4 space-y-2">
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange">Combination advantage</p>
                {opp.combination_advantage.capabilities_combined && (
                  <p className="text-sm text-white/85">{opp.combination_advantage.capabilities_combined.join(" + ")}</p>
                )}
                <p className="text-sm text-donkey-grey">{opp.combination_advantage.why_valuable}</p>
                <p className="text-sm text-donkey-grey"><span className="text-white/70">Hard to copy: </span>{opp.combination_advantage.why_hard_to_copy}</p>
                <p className="text-sm text-donkey-grey"><span className="text-white/70">Best addition: </span>{opp.combination_advantage.best_complementary_addition}</p>
              </div>
            )}
            {opp.transferable_capabilities && <Field label="Durable transferable capabilities"><List items={opp.transferable_capabilities} /></Field>}
            {opp.trade_offs && (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Trade-offs (gain vs accept)</p>
                <ul className="space-y-2">
                  {opp.trade_offs.map((t, i) => (
                    <li key={i} className="text-sm flex flex-col sm:flex-row sm:gap-3 rounded-lg bg-donkey-bg border border-white/10 p-3">
                      <span className="text-donkey-green flex-1">+ {t.gain}</span>
                      <span className="text-donkey-amber flex-1">− {t.accept}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="customer" className="space-y-5">
            <Field label="The customer">{opp.customer}</Field>
            <Field label="Their problem">{opp.customer_problem}</Field>
            <Field label="What they use instead"><List items={opp.competitors} /></Field>
            <Field label="Possible differentiation">{opp.differentiation}</Field>
          </TabsContent>

          <TabsContent value="offer" className="space-y-5">
            <Field label="Suggested initial offer">{opp.initial_offer}</Field>
            <Field label="Pricing model">{opp.pricing_model}</Field>
            <Field label="What you need to start"><List items={opp.what_you_need} /></Field>
          </TabsContent>

          <TabsContent value="evidence" className="space-y-5">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Market findings</p>
              {(opp.market_findings && opp.market_findings.length > 0) ? (
                <ul className="space-y-3">
                  {opp.market_findings.map((f, i) => {
                    const fAuth = f.source_authority === "high" ? ["high authority", "text-donkey-green border-donkey-green/40"]
                      : f.source_authority === "signal_only" ? ["signal only", "text-donkey-amber border-donkey-amber/40"]
                      : f.source_authority ? ["moderate", "text-donkey-grey border-white/20"] : null;
                    return (
                    <li key={i} className="rounded-xl bg-donkey-bg border border-white/10 p-3" data-testid="market-finding">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <EvidenceChip evidenceClass={f.evidence_class} />
                        {fAuth && <span className={`text-[10px] uppercase tracking-wide rounded border px-1.5 py-0.5 ${fAuth[1]}`}>{fAuth[0]}</span>}
                        {f.stance && <span className="text-[10px] uppercase tracking-wide text-donkey-grey">{f.stance.replace(/_/g, " ")}</span>}
                        {f.topic && <span className="text-[10px] text-donkey-grey">· {f.topic}</span>}
                      </div>
                      <p className="text-sm text-white/90 leading-relaxed">{f.finding}</p>
                      {(f.source_establishes || f.donkey_infers || f.remains_unknown) && (
                        <div className="mt-2 space-y-1 text-xs border-l-2 border-white/10 pl-3">
                          {f.source_establishes && <p><span className="text-donkey-green/90 font-medium">What the source establishes: </span><span className="text-white/70">{f.source_establishes}</span></p>}
                          {f.donkey_infers && <p><span className="text-donkey-orange font-medium">What the Donkey infers: </span><span className="text-white/70">{f.donkey_infers}</span></p>}
                          {f.remains_unknown && <p><span className="text-donkey-grey font-medium">What remains unknown: </span><span className="text-white/70">{f.remains_unknown}</span></p>}
                        </div>
                      )}
                      {f.source_url && (
                        <a href={f.source_url} target="_blank" rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-xs text-donkey-orange hover:underline break-all"
                          data-testid="citation-link">
                          <ExternalLink className="w-3 h-3 shrink-0" /> {f.source_url}
                          {f.retrieved_at && <span className="text-donkey-grey ml-1">· retrieved {String(f.retrieved_at).slice(0, 10)}</span>}
                        </a>
                      )}
                    </li>
                  );})}
                </ul>
              ) : (
                <p className="text-sm text-donkey-grey">No live market findings for this opportunity — scored on your evidence and honest inference.</p>
              )}
            </div>
            {opp.sources && opp.sources.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Sources retrieved (Tavily)</p>
                <ul className="space-y-2">
                  {opp.sources.map((s, i) => {
                    const authTone = s.authority === "high" ? "text-donkey-green border-donkey-green/40"
                      : s.authority === "signal_only" ? "text-donkey-amber border-donkey-amber/40"
                      : "text-donkey-grey border-white/20";
                    const authLabel = s.authority === "high" ? "high authority"
                      : s.authority === "signal_only" ? "signal only" : "moderate";
                    return (
                    <li key={i} className="text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                          className="text-donkey-orange hover:underline" data-testid="source-link">{s.title || s.url}</a>
                        {s.authority && <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-wide ${authTone}`} data-testid="source-authority">{authLabel}</span>}
                      </span>
                      <span className="block text-donkey-grey mt-0.5">{s.excerpt?.slice(0, 160)}…</span>
                    </li>
                  );})}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="adaptability" className="space-y-5">
            {opp.adaptability ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-xs uppercase tracking-[0.15em] text-donkey-grey">Resilience</span>
                  <ConfidenceBadge level={opp.adaptability.resilience} />
                </div>
                <p className="text-sm text-white/85">{opp.adaptability.summary}</p>
                <ul className="space-y-2">
                  {(opp.adaptability.stresses || []).map((st, i) => (
                    <li key={i} className="rounded-lg bg-donkey-bg border border-white/10 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-white/90 capitalize">{st.scenario}</span>
                        <span className={`text-xs ${st.can_adapt ? "text-donkey-green" : "text-donkey-red"}`}>{st.can_adapt ? "can adapt" : "fragile"}</span>
                      </div>
                      <p className="text-xs text-donkey-grey mt-1">{st.impact}</p>
                    </li>
                  ))}
                </ul>
              </>
            ) : <p className="text-sm text-donkey-grey">Adaptability not available for this report.</p>}
          </TabsContent>

          <TabsContent value="paths" className="space-y-4">
            {opp.future_paths ? ["conservative", "workable", "failure"].map((k) => {
              const p = opp.future_paths[k]; if (!p) return null;
              const col = k === "workable" ? "border-donkey-green/30" : k === "failure" ? "border-donkey-red/30" : "border-white/10";
              return (
                <div key={k} className={`rounded-xl bg-donkey-bg border ${col} p-4`}>
                  <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange mb-2">{k} path</p>
                  <p className="text-sm text-white/85 mb-2">{p.cause}</p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-donkey-grey">
                    <p><span className="text-white/60">Early signs: </span>{p.early_indicators}</p>
                    <p><span className="text-white/60">Money at risk: </span>{p.financial_exposure}</p>
                    <p><span className="text-white/60">Time at risk: </span>{p.time_exposure}</p>
                    <p><span className="text-donkey-green">Continue if: </span>{p.continue_if}</p>
                    <p><span className="text-donkey-amber">Change if: </span>{p.change_if}</p>
                    <p><span className="text-donkey-red">Stop if: </span>{p.stop_if}</p>
                  </div>
                </div>
              );
            }) : <p className="text-sm text-donkey-grey">Path comparison not available.</p>}
          </TabsContent>

          <TabsContent value="evidenceplan" className="space-y-5">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">First Evidence Plan — each step resolves an uncertainty</p>
              <ul className="space-y-2">
                {(opp.first_evidence_plan || []).map((st, i) => (
                  <li key={i} className="rounded-lg bg-donkey-bg border border-white/10 p-3">
                    <p className="text-sm text-white/90">{st.action}</p>
                    <p className="text-xs text-donkey-grey mt-1"><span className="text-white/60">Tests: </span>{st.uncertainty_addressed}</p>
                    <p className="text-xs text-donkey-green mt-0.5">Real signal: {st.success_signal}</p>
                  </li>
                ))}
              </ul>
            </div>
            {opp.minimum_loss_limit && (
              <div className="rounded-xl bg-donkey-red/10 border border-donkey-red/25 p-4 text-sm text-white/90 space-y-1">
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-red mb-1">Minimum loss limit</p>
                <p>Max spend before review: <span className="font-mono">{opp.minimum_loss_limit.max_money_gbp}</span> · Max hours: <span className="font-mono">{opp.minimum_loss_limit.max_hours}</span></p>
                <p className="text-donkey-grey">Evidence before spending more: {opp.minimum_loss_limit.evidence_before_more_spend}</p>
                <p className="text-donkey-grey">Pause if: {opp.minimum_loss_limit.pause_if}</p>
                <p className="text-donkey-grey">Abandon if: {opp.minimum_loss_limit.abandon_if}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="validation" className="space-y-5">
            <Field label="Seven-day validation test"><List items={opp.seven_day_test} /></Field>
            <Field label="Route to your first five customers"><List items={opp.route_to_first_customers} /></Field>
          </TabsContent>

          <TabsContent value="numbers" className="space-y-1">
            {Object.entries(SCORE_LABELS).map(([k, l]) => {
              const d = s[k] || {};
              return (
                <div key={k} className="py-1">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <ConfidenceBadge level={d.confidence} />
                    <EvidenceChip evidenceClass={d.evidence_class} />
                  </div>
                  <ScoreBar label={l} value={sc(d)} explanation={d.reason} />
                </div>
              );
            })}
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <Field label="Startup cost range">{opp.startup_cost_range}</Field>
              <Field label="Upfront spend range">{opp.upfront_spend_range}</Field>
            </div>
          </TabsContent>

          <TabsContent value="obstacles" className="space-y-5">
            <Field label="Key assumptions"><List items={opp.key_assumptions} /></Field>
            <Field label="Main weaknesses"><List items={opp.weaknesses} /></Field>
            <Field label="Important unknowns"><List items={opp.unknowns} /></Field>
            <Field label="Legal & operational considerations"><List items={opp.legal_operational} /></Field>
            {opp.founder_dependency && (
              <div className="rounded-xl bg-donkey-bg border border-white/10 p-4">
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange mb-1.5">Founder dependency: {opp.founder_dependency.level}</p>
                <p className="text-sm text-donkey-grey">{opp.founder_dependency.explanation}</p>
              </div>
            )}
            {opp.concentration_risks && opp.concentration_risks.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Concentration risks</p>
                <ul className="space-y-2">
                  {opp.concentration_risks.map((c, i) => (
                    <li key={i} className="text-sm rounded-lg bg-donkey-bg border border-white/10 p-3">
                      <span className="text-white/90 capitalize">{c.type}: </span><span className="text-donkey-grey">{c.risk}</span>
                      <p className="text-xs text-donkey-green mt-1">Reduce by: {c.mitigation}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {opp.control_map && (
              <div className="grid sm:grid-cols-3 gap-3">
                {[["under_control", "You control", "text-donkey-green"], ["influenceable", "You influence", "text-donkey-amber"], ["outside_control", "Outside control", "text-donkey-red"]].map(([k, l, c]) => (
                  <div key={k} className="rounded-lg bg-donkey-bg border border-white/10 p-3">
                    <p className={`text-xs uppercase tracking-wide mb-2 ${c}`}>{l}</p>
                    <List items={opp.control_map[k]} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="plan" className="space-y-5">
            <Field label="Thirty-day action plan"><List items={opp.thirty_day_plan} /></Field>
          </TabsContent>

          <TabsContent value="verdict" className="space-y-5">
            <VerdictBadge label={opp.verdict_label} />
            <p className="text-donkey-warm/90 leading-relaxed">{opp.verdict_explanation}</p>
            <VerdictDimensions dims={opp.verdict_dimensions} />
            <MismatchCallout mismatch={opp.founder_market_mismatch} />
            <a href={`/validate/${reportId}?opp=${index}`}
              className="inline-flex items-center gap-2 rounded-full bg-donkey-orange text-donkey-black font-semibold px-5 py-2.5 text-sm hover:bg-donkey-deeporange transition-colors"
              data-testid="prove-opportunity-btn">
              <Trophy className="w-4 h-4" /> Test this idea before spending money
            </a>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Strongest evidence"><span className="text-donkey-green">{opp.strongest_evidence}</span></Field>
              <Field label="Largest weakness"><span className="text-donkey-amber">{opp.largest_weakness}</span></Field>
            </div>
            <Field label="Assumptions that matter most"><List items={opp.assumptions_that_matter} /></Field>
            <Field label="What would change the verdict">{opp.what_would_change_verdict}</Field>
            {opp.final_verdict_answers && (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 rounded-xl bg-donkey-bg border border-white/10 p-4">
                {[["can_deliver", "Can they deliver it?"], ["customer_reason_to_buy", "Real reason to buy?"], ["can_reach_customer", "Can they reach the customer?"], ["testable_safely", "Testable without big risk?"], ["daily_activity_fits", "Daily work fits them?"], ["survives_change", "Survives foreseeable change?"], ["most_likely_to_kill", "Most likely to kill it"], ["next_evidence", "Next evidence to get"], ["before_spending_more", "True before spending more"]].map(([k, l]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase tracking-wide text-donkey-grey">{l}</p>
                    <p className="text-sm text-white/90">{opp.final_verdict_answers[k]}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-donkey-green/10 border border-donkey-green/25 p-4">
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-green mb-1.5">Recommended next action</p>
                <p className="text-sm text-white/90">{opp.recommended_next_action}</p>
              </div>
              <div className="rounded-xl bg-donkey-red/10 border border-donkey-red/25 p-4">
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-red mb-1.5">What not to do yet</p>
                <p className="text-sm text-white/90">{opp.what_not_to_do_yet}</p>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>

      <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap gap-3">
        <Button onClick={() => onSelect(index)} disabled={selected}
          className="bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold"
          data-testid="select-opportunity-btn">
          {selected ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Selected</> : "Select this opportunity"}
        </Button>
      </div>
    </div>
  );
}

function ChatPanel({ reportId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/reports/${reportId}/chat`).then(({ data }) => setMessages(data.messages || []));
  }, [reportId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const suggestions = [
    "Why did you choose this?", "What is the weakest assumption?",
    "How could I start for less?", "Who should I contact first?",
    "What would make you reject this idea?", "Give me a simpler version.",
  ];

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/chat`, { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[440px] z-50 glass border-l border-white/10 flex flex-col" data-testid="chat-panel">
      <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
        <div className="flex items-center gap-2">
          <DonkeyMark size={26} /><span className="font-heading font-semibold text-donkey-warm">Ask the Donkey</span>
        </div>
        <button onClick={onClose} className="text-donkey-grey hover:text-donkey-warm" data-testid="chat-close"><X /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-donkey-grey">Ask me anything about your report. I'll push back where your reasoning is thin.</p>
            <div className="flex flex-col gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)}
                  className="text-left text-sm text-donkey-warm/80 rounded-lg border border-white/10 bg-donkey-deep px-3 py-2 hover:border-donkey-orange/40 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex gap-2"}>
            {m.role === "assistant" && <DonkeyMark size={24} className="shrink-0 mt-1" />}
            <div className={`rounded-2xl px-3.5 py-2.5 text-sm max-w-[85%] leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-donkey-orange/15 border border-donkey-orange/25 text-donkey-warm"
                : "card-donkey text-donkey-warm/90"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && <div className="flex gap-2"><DonkeyMark size={24} /><div className="card-donkey rounded-2xl px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-donkey-orange" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="p-4 border-t border-white/10 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask a question…" className="flex-1 bg-donkey-deep border border-white/15 rounded-lg px-3 py-2 text-sm text-donkey-warm outline-none focus:border-donkey-orange"
          data-testid="chat-input" />
        <Button onClick={() => send()} disabled={busy} className="bg-donkey-orange text-donkey-black px-3" data-testid="chat-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [confirmOpp, setConfirmOpp] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineFailed, setRefineFailed] = useState(false);
  const [submittingRefine, setSubmittingRefine] = useState(false);
  const refinePollRef = useRef(null);

  const startRefinePoll = () => {
    if (refinePollRef.current) clearInterval(refinePollRef.current);
    refinePollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/reports/${id}/status`);
        if (data.refine_status === "failed") {
          clearInterval(refinePollRef.current);
          setRefineFailed(true);
        } else if (!data.refine_status) {
          clearInterval(refinePollRef.current);
          setRefining(false);
          setRefineFailed(false);
          await load();
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const openConfirm = (index) => setConfirmOpp(index);

  const doSelect = async () => {
    const index = confirmOpp;
    setConfirmOpp(null);
    await selectOpp(index);
  };

  const submitRefine = async () => {
    setSubmittingRefine(true);
    try {
      await api.post(`/reports/${id}/refine`, { feedback });
      setRefineOpen(false);
      setRefining(true);
      setRefineFailed(false);
      startRefinePoll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmittingRefine(false); }
  };

  const retryRefine = async () => {
    setRefineFailed(false);
    try {
      await api.post(`/reports/${id}/refine`, { feedback });
      startRefinePoll();
    } catch (e) { setRefineFailed(true); }
  };

  const seeDetail = (index) => {
    setShowDetail(true);
    setTimeout(() => document.getElementById(`opp-${index}`)?.scrollIntoView({ behavior: "smooth" }), 120);
  };
  useEffect(() => () => { if (refinePollRef.current) clearInterval(refinePollRef.current); }, []);

  const load = async () => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      if (data.gen_status && data.gen_status !== "complete") {
        navigate(`/analysis/${id}`, { replace: true });
        return;
      }
      setReport(data);
      setSelectedOpp(data.selected_opportunity ?? null);
      if (data.refine_status === "generating") { setRefining(true); startRefinePoll(); }
    } catch (e) {
      toast.error("Could not load report.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const unlock = async () => {
    if (!user) { navigate(`/register?next=/report/${id}`); return; }
    // Standalone Verdict is no longer sold — it is now included in Prove. Send to pricing.
    navigate("/pricing");
  };

  const selectOpp = async (index) => {
    try {
      const { data } = await api.post(`/reports/${id}/select`, { opportunity_index: index });
      setSelectedOpp(index);
      toast.success("Opportunity selected. Workspace ready.");
      navigate(`/workspace/${data.workspace_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading || !report) {
    return (
      <div className="min-h-screen bg-donkey-bg grain flex flex-col items-center justify-center gap-5">
        <DonkeyMark size={56} className="animate-pulse-soft" />
        <p className="text-donkey-grey">Fetching the verdict…</p>
      </div>
    );
  }

  const snap = report.snapshot;

  return (
    <div className="min-h-screen bg-donkey-bg grain">
      <div className="glass border-b border-white/10 sticky top-0 z-30 print:hidden">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between gap-2">
          <Logo />
          <div className="flex items-center gap-2 shrink-0">
            {report.unlocked && (
              <>
                <Button variant="ghost" size="sm" onClick={() => window.print()}
                  className="text-donkey-grey hover:text-donkey-warm px-2 sm:px-3" data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">PDF</span>
                </Button>
                <Button size="sm" onClick={() => setShowChat(true)}
                  className="bg-donkey-orange text-donkey-black font-semibold px-2.5 sm:px-3" data-testid="open-chat-btn">
                  <MessageSquare className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Ask the Donkey</span>
                </Button>
              </>
            )}
            {report.is_owner && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}
                className="text-donkey-grey hover:text-donkey-warm px-2 sm:px-3">
                <LayoutDashboard className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-10">
        {!report.unlocked ? (
          /* ---------- SNAPSHOT ---------- */
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-3">Free Opportunity Snapshot</p>
            <div className="grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2 card-donkey rounded-2xl p-8">
                <VerdictBadge label={snap?.verdict_label} />
                <h1 className="font-heading font-bold text-3xl text-donkey-warm mt-4">{snap?.title}</h1>
                <p className="text-donkey-grey mt-3 leading-relaxed">{snap?.proposition}</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <Field label="Why it might suit you">{snap?.why_you}</Field>
                </div>
                <div className="mt-5">
                  <Field label="One major concern"><span className="text-donkey-amber">{snap?.main_concern}</span></Field>
                </div>
              </div>
              <div className="card-donkey rounded-2xl p-6 text-center">
                <ScoreRing value={snap?.personal_fit} size={120} />
                <p className="text-sm text-donkey-grey mt-3">Personal fit</p>
              </div>
            </div>

            {/* Locked preview */}
            <div className="mt-8 relative">
              <div className="grid md:grid-cols-3 gap-4 blur-sm select-none pointer-events-none" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="card-donkey rounded-2xl p-6 h-52">
                    <div className="h-3 w-24 bg-white/15 rounded mb-3" />
                    <div className="h-5 w-40 bg-white/20 rounded mb-4" />
                    <div className="space-y-2">
                      {[0, 1, 2, 3].map((j) => <div key={j} className="h-2.5 w-full bg-white/10 rounded" />)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center card-donkey rounded-2xl p-8 max-w-md mx-4 border-donkey-orange/30">
                  <Lock className="w-8 h-8 text-donkey-orange mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-xl text-donkey-warm">
                    {snap?.opportunity_count || 3} opportunities are ready
                  </h3>
                  <p className="text-sm text-donkey-grey mt-2">
                    Full scores, evidence, seven-day tests, thirty-day plans and the complete verdict on each — included in Prove.
                  </p>
                  <Button onClick={unlock} disabled={unlocking}
                    className="mt-6 bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold h-12 px-7 w-full"
                    data-testid="unlock-verdict-btn">
                    Start with Prove — £29 <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <button onClick={() => navigate("/pricing")} className="mt-3 text-xs text-donkey-grey hover:text-donkey-warm">
                    See all plans →
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ---------- FULL VERDICT ---------- */
          <div>
            <div className="flex items-center gap-4 mb-8">
              <DonkeyMascot size={72} />
              <div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-donkey-warm">The Donkey's Verdict</h1>
                <p className="text-donkey-grey mt-1 text-sm">Three business ideas built around you. Pick the one you'd like to explore first — it's included in your plan.</p>
              </div>
            </div>

            {report.analysis?.recommendation && (
              <div className="card-donkey rounded-2xl p-6 mb-8 border-l-2 border-donkey-green" data-testid="donkey-recommends">
                <p className="text-xs uppercase tracking-[0.18em] text-donkey-green mb-1.5 flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5" /> The Donkey recommends
                </p>
                <p className="text-white/90 text-sm leading-relaxed">{report.analysis.recommendation}</p>
              </div>
            )}

            {/* Three clear choices — the decision, up front */}
            <h2 className="font-heading font-semibold text-xl text-donkey-warm mb-1">Your three opportunities</h2>
            <p className="text-sm text-donkey-grey mb-5 max-w-2xl">
              Choose the one you'd like to test first. Choosing won't charge you or launch anything — you can still read the others afterwards.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {report.analysis?.opportunities?.map((opp, i) => (
                <div key={i} className={`card-donkey rounded-2xl p-6 flex flex-col ${selectedOpp === i ? "border-donkey-orange/60" : ""}`} data-testid={`choice-card-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange">{opp.rank_label}</p>
                    <ScoreRing value={opp.overall} size={52} />
                  </div>
                  <h3 className="font-heading font-semibold text-donkey-warm">{opp.name}</h3>
                  <p className="text-sm text-donkey-grey mt-2 line-clamp-2">{opp.proposition}</p>
                  {opp.why_you && (
                    <p className="text-xs text-donkey-warm/80 mt-3 line-clamp-3">
                      <span className="text-donkey-grey">Why it suits you: </span>{opp.why_you}
                    </p>
                  )}
                  <div className="mt-3"><VerdictBadge label={opp.verdict_label} /></div>
                  <div className="mt-5 flex flex-col gap-2">
                    <Button onClick={() => openConfirm(i)}
                      className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-semibold w-full"
                      data-testid={`choose-opp-${i}`}>
                      {selectedOpp === i ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Selected</> : "Choose this opportunity"}
                    </Button>
                    <button onClick={() => seeDetail(i)} className="text-xs text-donkey-grey hover:text-donkey-warm" data-testid={`see-detail-${i}`}>
                      See the detail →
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* None of these feel right — one included rethink */}
            {!report.refinement_used ? (
              <div className="mb-8">
                <button onClick={() => { setFeedback(""); setRefineOpen(true); }}
                  className="text-sm text-donkey-grey hover:text-donkey-warm underline underline-offset-4"
                  data-testid="none-feel-right-btn">
                  None of these feel right — ask the Donkey to rethink
                </button>
              </div>
            ) : (
              <div className="mb-8 text-sm text-donkey-grey flex flex-wrap items-center gap-2" data-testid="refinement-used-note">
                <span>You've used your included rethink for this Verdict.</span>
                {report.previous_opportunities?.length > 0 && (
                  <button onClick={() => setShowPrevious((s) => !s)} className="text-donkey-orange hover:underline" data-testid="toggle-previous-btn">
                    {showPrevious ? "Hide your earlier ideas" : "View your earlier ideas"}
                  </button>
                )}
              </div>
            )}

            {showPrevious && report.previous_opportunities?.length > 0 && (
              <div className="card-donkey rounded-2xl p-6 mb-8" data-testid="previous-opportunities">
                <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-4">Your earlier ideas (before the rethink)</p>
                <div className="grid md:grid-cols-3 gap-4">
                  {report.previous_opportunities.map((opp, i) => (
                    <div key={i} className="rounded-xl bg-donkey-bg border border-white/10 p-4">
                      <p className="text-xs uppercase tracking-[0.15em] text-donkey-grey mb-1">{opp.rank_label}</p>
                      <h4 className="font-heading font-semibold text-donkey-warm text-sm">{opp.name}</h4>
                      <p className="text-xs text-donkey-grey mt-1.5 line-clamp-3">{opp.proposition}</p>
                      <div className="mt-2"><VerdictBadge label={opp.verdict_label} /></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progressive disclosure — the deep analysis */}
            <div className="mb-6">
              <button onClick={() => setShowDetail((s) => !s)}
                className="inline-flex items-center gap-2 rounded-full border border-donkey-line px-5 py-2.5 text-sm text-donkey-warm hover:bg-white/5 transition-colors"
                data-testid="toggle-full-analysis">
                {showDetail ? "Hide full analysis" : "View full analysis"}
                <ChevronDown className={`w-4 h-4 transition-transform ${showDetail ? "rotate-180" : ""}`} />
              </button>
              <p className="text-xs text-donkey-grey mt-2">Scores, evidence, the seven-day test and the full breakdown for each idea.</p>
            </div>

            {showDetail && (
              <div data-testid="full-analysis" className="space-y-8">
                {report.analysis?.overall_note && (
                  <div className="card-donkey rounded-2xl p-6 border-l-2 border-donkey-orange">
                    <p className="text-donkey-warm/90 text-sm leading-relaxed">{report.analysis.overall_note}</p>
                  </div>
                )}

                {report.research_status && (
                  <div className="flex items-start gap-2.5 text-xs rounded-xl border p-3.5 border-white/10 bg-donkey-bg" data-testid="research-status">
                    <Info className="w-4 h-4 text-donkey-orange shrink-0 mt-0.5" />
                    <p className="text-donkey-grey leading-relaxed">
                      {report.research_status === "live" && (
                        <><span className="text-white">Live market research connected.</span> {report.research_calls_used} Tavily search{report.research_calls_used === 1 ? "" : "es"} ran for this report. Claims marked <span className="text-donkey-green">public source evidence</span> open a real source. General interest is never treated as proof of demand.</>
                      )}
                      {(report.research_status === "fallback" || report.research_status === "unavailable") && (
                        <><span className="text-white">Live research unavailable for this report</span> — scores rest on your evidence and honest inference only. No citations were invented.</>
                      )}
                      {report.research_status === "pre_research" && (
                        <><span className="text-white">This report was generated before live research was connected.</span> Regenerate it to add cited market evidence.</>
                      )}
                    </p>
                  </div>
                )}

                {/* Venture Tournament comparison */}
                <div className="card-donkey rounded-2xl p-6 overflow-x-auto" data-testid="tournament-table">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-4 h-4 text-donkey-orange" />
                    <h2 className="font-heading font-semibold text-white">The Venture Tournament</h2>
                  </div>
                  <p className="text-xs text-donkey-grey mb-4">
                    Every score carries a confidence level and an evidence class. Claims are grounded in what you told
                    the Donkey, honest inference and — where available — live market research. General interest is never
                    treated as proof of demand, and nothing is invented.
                  </p>
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left font-medium text-donkey-grey py-2 pr-3">Dimension</th>
                        {report.analysis?.opportunities?.map((o, i) => (
                          <th key={i} className="text-center font-medium text-white px-2 py-2">{o.rank_label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(SCORE_LABELS).map(([k, l]) => (
                        <tr key={k} className="border-b border-white/5">
                          <td className="text-donkey-grey py-2 pr-3">{l}</td>
                          {report.analysis?.opportunities?.map((o, i) => {
                            const d = (o.scores || {})[k] || {};
                            const v = sc(d) || 0;
                            const col = v >= 70 ? "#16A34A" : v >= 45 ? "#D97706" : "#DC2626";
                            return (
                              <td key={i} className="text-center px-2 py-2">
                                <span className="font-mono" style={{ color: col }}>{v}</span>
                                {d.confidence && <span className="block text-[9px] text-donkey-grey">{d.confidence}</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      <tr className="border-t border-white/15">
                        <td className="font-semibold text-white py-2 pr-3">Overall</td>
                        {report.analysis?.opportunities?.map((o, i) => (
                          <td key={i} className="text-center font-mono font-semibold text-donkey-orange py-2">{o.overall}</td>
                        ))}
                      </tr>
                      <tr>
                        <td className="text-donkey-grey py-2 pr-3">Verdict</td>
                        {report.analysis?.opportunities?.map((o, i) => (
                          <td key={i} className="text-center py-2"><VerdictBadge label={o.verdict_label} /></td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="space-y-8">
                  {report.analysis?.opportunities?.map((opp, i) => (
                    <div key={i} id={`opp-${i}`} className="scroll-mt-24">
                      <OpportunityDetail opp={opp} index={i} reportId={id} onSelect={openConfirm} selected={selectedOpp === i} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 text-center card-donkey rounded-2xl p-6 print:hidden">
              <p className="text-xs text-donkey-grey max-w-2xl mx-auto">
                The Honest Donkey provides research and decision support, not financial, legal or professional advice.
                All opportunities require independent validation.
              </p>
            </div>
          </div>
        )}
      </div>

      {showChat && <ChatPanel reportId={id} onClose={() => setShowChat(false)} />}

      {/* Reassurance before choosing an opportunity */}
      {confirmOpp != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="choose-confirm">
          <div className="card-donkey rounded-2xl p-7 max-w-md w-full border-donkey-orange/30">
            <h3 className="font-heading font-bold text-xl text-donkey-warm">Explore this idea next?</h3>
            <p className="text-sm text-donkey-grey mt-3 leading-relaxed">
              You're choosing <span className="text-donkey-warm font-medium">{report.analysis?.opportunities?.[confirmOpp]?.name}</span> to explore next.
              You can still read your other opportunities, and choosing does not charge you or automatically launch a business.
            </p>
            <div className="mt-6 flex gap-3">
              <Button onClick={doSelect} className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-semibold flex-1" data-testid="confirm-choose-btn">
                Yes, choose this
              </Button>
              <Button variant="outline" onClick={() => setConfirmOpp(null)} className="border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5" data-testid="cancel-choose-btn">
                Not yet
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refinement request */}
      {refineOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="refine-dialog">
          <div className="card-donkey rounded-2xl p-7 max-w-md w-full border-donkey-orange/30">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-donkey-orange" />
              <h3 className="font-heading font-bold text-xl text-donkey-warm">Ask the Donkey to rethink</h3>
            </div>
            <p className="text-sm text-donkey-grey leading-relaxed">
              You get one free rethink, included in your plan. Tell the Donkey what didn't fit and it'll suggest three new ideas. Your current ideas stay saved.
            </p>
            <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g. Too expensive to start, not really my thing, wrong kind of customers…"
              className="mt-4 w-full min-h-[110px] bg-donkey-deep border border-white/15 rounded-lg px-3 py-2.5 text-sm text-donkey-warm outline-none focus:border-donkey-orange resize-none"
              data-testid="refine-feedback" />
            <div className="mt-5 flex gap-3">
              <Button onClick={submitRefine} disabled={submittingRefine}
                className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-semibold flex-1" data-testid="refine-submit-btn">
                {submittingRefine ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Rethink my ideas"}
              </Button>
              <Button variant="outline" onClick={() => setRefineOpen(false)} className="border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5" data-testid="refine-cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rethinking in progress — trustworthy waiting state */}
      {refining && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-6 text-center bg-donkey-bg/95 backdrop-blur-sm grain" data-testid="refining-overlay">
          {refineFailed ? (
            <>
              <RefreshCw className="w-12 h-12 text-donkey-amber mb-5" />
              <h2 className="font-heading font-bold text-2xl text-donkey-warm">The rethink didn't finish.</h2>
              <p className="mt-3 text-donkey-grey max-w-md">Nothing was charged and your ideas are safe. Give it another go.</p>
              <div className="mt-7 flex gap-3">
                <Button onClick={retryRefine} className="bg-donkey-orange text-donkey-black font-semibold px-7 h-12" data-testid="refine-retry-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Try again
                </Button>
                <Button variant="outline" onClick={() => { setRefining(false); setRefineFailed(false); }} className="border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5">
                  Back to my Verdict
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="relative mb-8">
                <div className="absolute inset-0 rounded-full bg-donkey-orange/20 blur-2xl animate-pulse-soft" />
                <DonkeyMark size={72} className="relative animate-pulse-soft" />
              </div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">The Donkey is rethinking your ideas.</h2>
              <p className="mt-3 text-donkey-grey max-w-md">This can take a few minutes. You can safely leave this page and come back — we'll keep working.</p>
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="mt-8 border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5">
                Leave and come back later
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
